<template>
  <div>
    <ReviewAndSubmitComponent :updateKey="false" :infoKey="true">
      <!-- Claim details -->
      <h3 class="review-subheading">Claim Details</h3>
      <table class="review-and-submit-table">
        <tr class="info">
          <th>Member receiving services</th>
          <td>{{ selectedMemberName }}</td>
        </tr>
        <tr>
          <th>Claim type</th>
          <td>{{ selectedModality }}</td>
        </tr>
        <tr v-if="isClaim && selectedMemberAccountDetails !== null" class="info">
          <th>Payable to</th>
          <td>
            <ul>
              <li>{{ selectedMemberAccountDetails.accountHolderName }}</li>
              <li>{{ selectedMemberAccountDetails.bsb }}</li>
              <li>{{ selectedMemberAccountDetails.accountNumber }}</li>
            </ul>
          </td>
        </tr>
        <tr v-if="selectedProviderObject !== undefined">
          <th>Provider</th>
          <td>
            <ul>
              <li>{{ selectedProviderObject.providerId }}</li>
              <li>
                {{ getProviderDisplayName(selectedProviderObject.lastName, selectedProviderObject.firstName, selectedProviderObject.middleName) }}
              </li>
              <li>{{ titleCase(selectedProviderObject.addressLine1) }}</li>
              <li>{{ titleCase(selectedProviderObject.addressLine2) }}</li>
              <li>{{ titleCase(selectedProviderObject.addressLine3) }}</li>
              <li>
                {{ getAddressLocalityText(selectedProviderObject.suburb, selectedProviderObject.state, selectedProviderObject.postcode) }}
              </li>
            </ul>
          </td>
        </tr>
      </table>

      <!-- Claims -->
      <div v-for="(claimLine, index) in feature.step4.claimLines" :key="'claim-line-' + index">
        <h3 class="review-subheading">{{ getServiceClaimLineDescription(claimLine) }}</h3>
        <table class="review-and-submit-table">
          <tr>
            <th>Date of service</th>
            <td>{{ claimLine.serviceDate }}</td>
          </tr>
          <tr v-if="isToothIdRequired(claimLine.serviceId) && !isEmptyString(claimLine.toothId)">
            <th>Tooth number</th>
            <td>{{ claimLine.toothId }}</td>
          </tr>
          <tr>
            <th>Provider's fee</th>
            <td>{{ formatAsCurrency(claimLine.providerFee) }}</td>
          </tr>
        </table>

        <ul v-if="isClaim">
          <li
            v-for="(fileObj, i) in claimLine.uploadedFiles.files"
            :key="`claim-line-${index}-file-${i}-${fileObj.file ? fileObj.file.name : fileObj.name}`"
            :class="fileUploadStatus(fileObj.uploadStatus)"
          >
            <Spinner v-if="fileObj.uploadStatus === LoadingStatus.PENDING" />
            <div class="filename">
              {{ fileObj.file ? fileObj.file.name : fileObj.name }}
            </div>
          </li>
        </ul>
      </div>
    </ReviewAndSubmitComponent>

    <ApiLoading v-if="apiStatus.loading" :apiError="apiStatus.fail" class="loading-wrapper" :loadingMessage="loadingMessage" isMemberPortal />

    <template v-if="!apiStatus.loading || apiStatus.apiFail">
      <div class="confirmation-text">
        <p v-if="!isClaim">Get your quote based on the above information or go back to change the details.</p>
        <template v-else>
          <p>Almost done. Your claim will be assessed based on this information. If you need to change it, choose to go back. If you're happy with it, submit your claim.</p>
          <p>By submitting this claim you declare that:</p>
          <ol id="claims-declaration-list">
            <li>you have incurred the expenses,</li>
            <li>the information supplied is accurate,</li>
            <!-- prettier-ignore -->
            <li>the claim meets our <a href="https://www.defencehealth.com.au/Health-Insurance/Find-Providers-and-Hospitals/General-claiming-conditions" target="_blank">general claiming conditions</a>, and</li>
            <li>you authorise us to obtain any information from the provider to audit or verify this claim if required.</li>
          </ol>
          <p>If you’ve attached documents, they will be uploaded once the claim is submitted.</p>
          <p>
            Our
            <a href="https://www.defencehealth.com.au/getmedia/cd84a929-b49f-4d33-89a6-bd7f7aeebd31/4-4b-Privacy-Policy.pdf" target="_blank">privacy policy</a>
            details how we handle personal information, how to access or correct that information, and how to make a privacy complaint.
          </p>
        </template>
      </div>
      <div class="form-page-nav">
        <section>
          <button class="secondary" @click="updatePage(currentPage - 1)">Back</button>
          <button class="button" @click="isClaim ? submitClaim() : getQuote()">
            {{ isClaim ? 'Submit claim' : 'Get quote' }}
          </button>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { mapGetters } from 'vuex';
import Spinner from '@general/js/components/Spinner.vue';
import { DHLError, formatFormDate, getCurrentDate } from '@general/js/utils';
import { LoadingStatus } from '@general/js/enums';
import { VUEX_BENEFIT_QUOTES_CLAIMS } from '@/constants/VuexConstants';
import { BENEFIT_QUOTE_RESULT, CLAIMS_RESULT, CLAIMS_RESULT_KEEP_PRIVATE, CONTACT_US } from '@/router/routeNames';
import BenefitQuotesServices from '@/services/BenefitQuotes';
import ClaimsServices from '@/services/Claims';
import { FileUploadCategoryTypes } from '@/enums';
import { ReviewAndSubmitComponent } from '@/components';
import { isEmptyString } from '@/utils/DisplayUtils';
import GABQnMANCStepsMixins from './GABQnMANC_StepsMixins';

export default {
  name: 'GABQ-step-5',

  mixins: [GABQnMANCStepsMixins],

  components: {
    Spinner,
    ReviewAndSubmitComponent,
  },

  data() {
    return {
      CONTACT_US,
      uploadingFiles: false,
      uploadFileProgress: 0,
      failedUpload: false,
      totalFiles: 0,
      timestamp: null,
      LoadingStatus,
    };
  },

  activated() {
    if (this.isClaim) {
      this.timestamp = Date.now();
      this.addFileUploadStatus();
    }
  },

  deactivated() {
    if (this.isClaim) {
      this.removeFileUploadStatus();
    }
  },

  computed: {
    ...mapGetters('BenefitQuotesClaims', {
      selectedProviderFlexCode: VUEX_BENEFIT_QUOTES_CLAIMS.GETTERS.getSelectedProviderFlexCode,
    }),

    /**
     * Provider object that's selected for display purposes
     */
    selectedProviderObject() {
      const [providerId, subProviderIdentifierType] = this.feature.step3.selectedProvider.split('-');

      if (!isEmptyString(subProviderIdentifierType)) {
        return this.refData.providers.find((provider) => {
          return provider.providerId === providerId && provider.subProviderIdentifierType === subProviderIdentifierType;
        });
      }

      return this.refData.providers.find((provider) => {
        return provider.providerId === providerId;
      });
    },

    /**
     * Selected member's display name
     */
    selectedMemberName() {
      const selectedMemberObj = this.refData.members.find((member) => {
        return member.personCode === this.feature.step1.member;
      });

      return selectedMemberObj ? this.titleCase(`${selectedMemberObj.firstName} ${selectedMemberObj.lastName}`) : '';
    },

    /**
     * Finds a modality in the Modalities array with the given ID, or empty string if there is no match.
     */
    selectedModality() {
      const selectedModality = this.modalities(this.feature.step1.member).find((modality) => {
        return modality.returnValue === this.feature.step1.modality;
      });
      return selectedModality ? selectedModality.display : '';
    },

    loadingMessage() {
      let message = '';
      if (this.uploadingFiles) {
        message = message.concat(`${this.uploadFileProgress}/${this.totalFiles} files have been uploaded. <br>`);
      }
      message = message.concat(`We are processing your request. It shouldn't be too long.`);

      return message;
    },

    fileUploadStatus() {
      return (uploadStatus) => {
        if (uploadStatus === LoadingStatus.PENDING) {
          return 'upload-pending';
        }
        if (uploadStatus === LoadingStatus.SUCCESS) {
          return 'uploaded';
        }
        if (uploadStatus === LoadingStatus.ERROR) {
          return 'upload-error';
        }
        return 'normal-file-view';
      };
    },
  },

  methods: {
    /**
     * File upload functionality for handling reuploads.
     * - Converts file into file object which contains 'uploaded' and 'uploadStatus' properties
     * - Used to track which files have been uploaded and which haven't
     * - Calculates total files
     * - Adds a blobNames empty array (so that files can be added when uploaded)
     */
    addFileUploadStatus() {
      this.uploadFileProgress = 0;
      this.totalFiles = 0;
      this.feature.step4.claimLines.forEach((claimLine) => {
        claimLine.uploadedFiles.files.forEach((file, fileIndex) => {
          // if it has already been converted into object, reset properties, otherwise, convert to object
          if (typeof file === 'object' && 'uploaded' in file) {
            file.uploaded = false;
            file.uploadStatus = null;
          } else {
            claimLine.uploadedFiles.files[fileIndex] = {
              file,
              uploaded: false,
              uploadStatus: null,
            };
          }
          this.totalFiles += 1;
        });
        claimLine.uploadedFiles.blobNames = [];
      });
    },

    /**
     * Removes the file upload status (changes back to storing the file only, removes upload status properties)
     * This is necessary for resetting the file structure to ensure functionality does not effect 'Enter service information' step when leaving this step
     */
    removeFileUploadStatus() {
      this.feature.step4.claimLines.forEach((claimLine) => {
        claimLine.uploadedFiles.files.forEach((fileObj, fileIndex) => {
          claimLine.uploadedFiles.files[fileIndex] = fileObj.file;
        });
      });
    },

    getQuote() {
      if (this.isCRM) {
        this.crmGetQuote();
      } else {
        this.cmsGetQuote();
      }
    },

    /**
     * Handles the Get Quote button being pressed.
     */
    async cmsGetQuote() {
      const getQuoteApiStatusObj = {
        fail: false,
        apiInProgress: true,
      };
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;
      this.$emit('getQuoteApiStatus', getQuoteApiStatusObj);

      const memberNumber = this.refData.memberNumber;
      const accessingPersonCode = this.feature.step1.member;
      const providerId = this.feature.step3.selectedProvider.split('-')[0];
      const providerFlexCode = this.selectedProviderFlexCode;
      const subProviderIdentifier = this.selectedProviderObject.subProviderIdentifier;
      const subProviderIdentifierType = this.selectedProviderObject.subProviderIdentifierType;
      const productCode = this.feature.step1.productCode;
      const services = cloneDeep(this.feature.step4.claimLines).map((service) => {
        // format for api
        return {
          serviceDate: this.formatFormDate(service.serviceDate),
          serviceId: service.serviceId,
          flexCode: service.flexCode,
          toothId: service.toothId,
          providerFee: Number(service.providerFee),
        };
      });

      try {
        const responseData = await BenefitQuotesServices.submitBenefitQuote(
          memberNumber,
          accessingPersonCode,
          providerId,
          providerFlexCode,
          subProviderIdentifier,
          subProviderIdentifierType,
          productCode,
          services,
        );

        // add missing provider data on responseData
        const providerData = this.refData.providers.find((provider) => {
          return provider.providerId === providerId;
        });
        responseData.provider = {
          ...responseData.provider,
          ...providerData,
          providerId,
        };

        this.$router.push({
          name: BENEFIT_QUOTE_RESULT,
          params: {
            quoteId: responseData.benefitQuoteId,
            accessingPersonCode,
          },
          state: JSON.parse(
            JSON.stringify({
              formData: responseData,
              refData: this.refData.content,
              productCode,
            }),
          ),
        });
      } catch (error) {
        this.apiStatus.fail = false; // Normally this would be set to `true` but we only want to show one top-level error if Submit Benefit Quote is the only API that fails.
        getQuoteApiStatusObj.fail = true;
      } finally {
        this.apiStatus.loading = false;
        getQuoteApiStatusObj.apiInProgress = false;
        this.$emit('getQuoteApiStatus', getQuoteApiStatusObj);
      }
    },

    submitClaim() {
      if (!this.isCRM) {
        this.cmsSubmitClaim();
      }
    },

    async cmsSubmitClaim() {
      const getClaimApiStatusObj = {
        fail: false,
        apiInProgress: true,
      };
      this.apiStatus.loading = true;
      this.apiStatus.fail = false;
      this.$emit('getQuoteApiStatus', getClaimApiStatusObj);

      const memberNumber = this.refData.memberNumber;
      const accessingPersonCode = this.feature.step1.member;
      const providerId = this.feature.step3.selectedProvider.split('-')[0];
      const providerFlexCode = this.selectedProviderFlexCode;
      const subProviderIdentifier = this.selectedProviderObject.subProviderIdentifier;
      const subProviderIdentifierType = this.selectedProviderObject.subProviderIdentifierType;
      const claimAccountId = this.selectedMemberAccountDetails.accountId;
      const productCode = this.feature.step1.productCode;

      try {
        this.$emit('submit', true);
        const fileUploadedFailed = await this.uploadFiles(this.feature.step4.claimLines, memberNumber, accessingPersonCode);
        if (fileUploadedFailed) {
          throw new DHLError('File upload error');
        }

        const serviceList = cloneDeep(this.feature.step4.claimLines).map((service) => {
          return {
            serviceDate: formatFormDate(service.serviceDate),
            serviceId: service.serviceId,
            flexCode: service.flexCode,
            toothId: service.toothId,
            providerFee: Number(service.providerFee),
            blobNames: service.uploadedFiles.blobNames,
          };
        });

        const response = await ClaimsServices.submitClaim(
          memberNumber,
          accessingPersonCode,
          providerId,
          providerFlexCode,
          subProviderIdentifier,
          subProviderIdentifierType,
          claimAccountId,
          productCode,
          serviceList,
        );

        const providerData = this.refData.providers.find((provider) => {
          return provider.providerId === providerId;
        });
        response.provider = {
          ...response.provider,
          ...providerData,
          providerId,
        };

        const bsb = this.selectedMemberAccountDetails.bsb !== '' ? this.selectedMemberAccountDetails.bsb : 'XXX - XXX';
        const accountNumber = this.selectedMemberAccountDetails.accountNumber !== '' ? this.selectedMemberAccountDetails.accountNumber : 'XXXX - XXXX';

        response.payableTo = [this.selectedMemberAccountDetails.accountHolderName, bsb, accountNumber];
        if (response.claimId) {
          this.$router.push({
            name: CLAIMS_RESULT,
            params: {
              quoteId: response.claimId,
              accessingPersonCode,
            },
            state: JSON.parse(
              JSON.stringify({
                formData: response,
                refData: this.refData.content,
                productCode,
              }),
            ),
          });
        } else {
          // Keep private claims recieve limited information back from the API, need to impute captured information
          response.claimDate = formatFormDate(getCurrentDate());
          response.memberFullName = this.selectedMemberName ?? ''; 
          response.modality = { claimTypeName: this.selectedModality ?? '' };

          this.$router.push({
            name: CLAIMS_RESULT_KEEP_PRIVATE,
            params: {},
            state: JSON.parse(JSON.stringify({ formData: response, refData: this.refData.content })),
          });
        }
      } catch (error) {
        this.apiStatus.fail = false;
        getClaimApiStatusObj.fail = true;
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        this.$emit('submit', false);
      } finally {
        this.apiStatus.loading = false;
        getClaimApiStatusObj.apiInProgress = false;
        this.$emit('getQuoteApiStatus', getClaimApiStatusObj);
      }
    },

    /**
     * Uploads files from all claimlines
     * Goes through all claimlines and all files in each claim line to upload
     */
    async uploadFiles(claimLines, memberNumber, accessingPersonCode) {
      this.uploadingFiles = true;
      let failed = false;
      for (let claimLineIndex = 0; claimLineIndex < claimLines.length; claimLineIndex++) {
        const claimLine = claimLines[claimLineIndex];

        if (claimLine.uploadedFiles && claimLine.uploadedFiles.files) {
          for (let fileIndex = 0; fileIndex < claimLine.uploadedFiles.files.length; fileIndex++) {
            const fileObj = claimLine.uploadedFiles.files[fileIndex];
            if (!fileObj.uploaded) {
              // need to use splice so that vuex can detect updates with its array items
              claimLine.uploadedFiles.files.splice(fileIndex, 1, {
                file: fileObj.file,
                uploaded: fileObj.uploaded,
                uploadStatus: LoadingStatus.PENDING,
              });
              // eslint-disable-next-line no-await-in-loop
              const blobName = await this.uploadFile(claimLine, fileObj.file, fileIndex, memberNumber, accessingPersonCode);
              if (!blobName) {
                failed = true;
                claimLine.uploadedFiles.files[fileIndex].uploadStatus = LoadingStatus.ERROR;
              } else {
                claimLine.uploadedFiles.blobNames.push(blobName);
                claimLine.uploadedFiles.files[fileIndex].uploadStatus = LoadingStatus.SUCCESS;
              }
            }
          }
        }
      }
      this.uploadingFiles = false;

      return failed;
    },

    /**
     * Upload an individual file
     * @returns the blobName (location of the file stored) or false if unsuccessful
     */
    async uploadFile(claimLine, file, fileIndex, memberNumber, accessingPersonCode) {
      try {
        const response = await ClaimsServices.submitFile(
          memberNumber,
          accessingPersonCode,
          FileUploadCategoryTypes.CLAIMS,
          file,
          this.timestamp,
          claimLine.serviceId,
          formatFormDate(claimLine.serviceDate),
          fileIndex,
        );
        if (response.blobName) {
          this.uploadFileProgress += 1;
          claimLine.uploadedFiles.files[fileIndex].uploaded = true;
          return response.blobName;
        }
        return false;
      } catch (err) {
        return false;
      }
    },

    /**
     * Generates the Service claim line description.
     */
    getServiceClaimLineDescription(claimLine) {
      if (claimLine.serviceId === '') {
        // `claimLine.serviceId` can be an empty string if the user is on the Review & Submit page, then returns to the Service Information step and removes the
        // service description for any Service claim line item.
        //
        // Note that returning an empty string in this case is to prevent console errors only. A proper value will be returned once the user re-selects a service
        // description and returns to the Review & Submit page.
        return '';
      }

      return `${claimLine.serviceId} - ${
        this.refData.services.find((obj) => {
          return obj.serviceId === claimLine.serviceId;
        }).serviceName
      }`;
    },

    formatFormDate,
  },
};
</script>

<style lang="scss" scoped>
:deep(.review-and-submit-table tr.info th::before) {
  @include tablet-and-above {
    top: 0 !important;
    padding-top: 32px !important;
    transform: unset !important;
  }
}

#claims-declaration-list {
  counter-reset: item;

  li {
    &::before {
      display: inline-block;
      content: counter(item, lower-alpha) ') ';
      counter-increment: item;
      margin-right: rem(16);
    }
  }
}
</style>
